import React from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { LANGUAGES, isSupportedLanguage, persistLanguage } from "../i18n";
import { API, useAuth } from "../App";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "./ui/dropdown-menu";

const LanguageSwitcher = ({ variant = "light" }) => {
  const { i18n } = useTranslation();
  const { isAuthenticated, token } = useAuth();
  const current = LANGUAGES.find(l => l.code === i18n.language?.split('-')[0]) || LANGUAGES[0];

  const change = async (code) => {
    if (!isSupportedLanguage(code)) return;

    // Persist first. This prevents the auth hydration listener from restoring
    // the previous language while i18next is switching.
    persistLanguage(code);
    await i18n.changeLanguage(code);

    // Persist to the user's profile so the preference follows the account too.
    if (isAuthenticated && token) {
      try {
        await axios.put(`${API}/user/language`, { language: code }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        // Local persistence remains available if the profile request fails.
      }
    }
  };

  const light = variant === "light";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium transition-colors ${
          light
            ? 'text-[#2E1D28] hover:bg-[#C084A0]/10'
            : 'text-white/90 hover:bg-white/10'
        }`}
        data-testid="lang-switcher-trigger"
      >
        <Globe className="w-4 h-4" strokeWidth={1.5} />
        <span className="hidden sm:inline">{current.native}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-2xl w-52">
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => change(lang.code)}
            className="flex items-center justify-between cursor-pointer"
            data-testid={`lang-option-${lang.code}`}
          >
            <span>{lang.native}</span>
            {current.code === lang.code && <Check className="w-4 h-4 text-[#C084A0]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
